import { defineStore } from "pinia";
import { ref } from "vue";
import { db } from "../js/firebase";
import {
  collection,
  doc,
  setDoc,
  getDocs,
  deleteDoc
} from "firebase/firestore";
import { logger } from '../services/loggerService';
import { initCalculatorSelections, computeCalculatorTotals } from '../services/pricingCalculatorService';

/**
 * Recursively strip `undefined` values from an object/array tree.
 * Firestore rejects any field whose value is literally `undefined` (it accepts
 * `null`). Since configs serialize the whole reactive form — including
 * AI-generated and partially-edited sections — a single nested undefined would
 * fail the entire setDoc(). Sanitizing here, the single write chokepoint,
 * protects every save path.
 */
function stripUndefined(value) {
  if (Array.isArray(value)) {
    return value.map(stripUndefined);
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndefined(v)])
    );
  }
  return value;
}

export const useFormBuilderStore = defineStore("formBuilder", () => {
  // State
  const loading = ref(false);
  const error = ref(null);
  const initialized = ref(false);
  const configs = ref({});

  // Firebase collection reference — kept as `intakeConfigs` to avoid data migration
  const intakeConfigsRef = collection(db, "intakeConfigs");

  // Actions
  /**
   * Load all form configurations from Firestore
   */
  async function loadConfigurations() {
    if (initialized.value) return;

    loading.value = true;
    error.value = null;

    try {
      logger.info('[FormBuilder] Loading form configurations');

      const snapshot = await getDocs(intakeConfigsRef);
      const loadedConfigs = {};

      snapshot.forEach((doc) => {
        const config = { id: doc.id, ...doc.data() };

        // Ensure enabled property exists (default to true for backwards compatibility)
        if (config.enabled === undefined) {
          config.enabled = true;
        }

        // Ensure icon property exists
        if (config.icon === undefined) {
          config.icon = "file";
        }

        // Ensure section types exist (backwards compatibility)
        if (config.sections) {
          config.sections.forEach(section => {
            if (!section.type) {
              section.type = 'standard';
            }
          });
        }

        loadedConfigs[doc.id] = config;
      });

      configs.value = loadedConfigs;
      initialized.value = true;
      logger.info('[FormBuilder] Configurations loaded successfully', {
        configCount: Object.keys(loadedConfigs).length
      });

    } catch (err) {
      logger.error('[FormBuilder] Failed to load configurations:', {
        error: err.message
      });
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Save a specific configuration to Firestore
   * @param {string} configId - The ID of the configuration
   * @param {Object} configData - The configuration data to save
   */
  async function saveConfiguration(configId, configData) {
    loading.value = true;
    error.value = null;

    try {
      logger.info('[FormBuilder] Saving configuration', {
        configId,
        sectionCount: configData.sections?.length || 0
      });

      // Prepare the config data for Firestore (strip nested undefined — Firestore rejects it)
      const configToSave = stripUndefined({
        id: configId,
        type: configId,
        name: configData.name || configId,
        category: (configData.category || "").toLowerCase(),
        enabled: configData.enabled !== undefined ? configData.enabled : true,
        icon: configData.icon || "file",
        sections: configData.sections || [],
        updatedAt: new Date().toISOString(),
        version: "2.0"
      });

      // Save to Firestore using the config ID as document ID
      const docRef = doc(intakeConfigsRef, configId);
      await setDoc(docRef, configToSave);

      // Update local state
      configs.value[configId] = { ...configToSave };

      logger.info('[FormBuilder] Configuration saved successfully', {
        configId,
        sectionCount: configToSave.sections.length
      });

      return { success: true };

    } catch (err) {
      logger.error('[FormBuilder] Failed to save configuration:', {
        configId,
        error: err.message
      });
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get a specific configuration by ID
   * @param {string} configId - The ID of the configuration to retrieve
   * @returns {Object} The configuration object
   */
  function getConfiguration(configId) {
    return configs.value[configId] || null;
  }

  /**
   * Add a new configuration type
   * @param {string} configId - The unique identifier for the new configuration
   * @param {Object} configData - The configuration data
   */
  async function addConfiguration(configId, configData) {
    loading.value = true;
    error.value = null;

    try {
      logger.info('[FormBuilder] Adding new configuration', {
        configId,
        name: configData.name
      });

      // Prepare the config data for Firestore (strip nested undefined — Firestore rejects it)
      const configToSave = stripUndefined({
        id: configId,
        type: configId,
        name: configData.name || configId,
        category: (configData.category || "").toLowerCase(),
        description: configData.description || "",
        enabled: configData.enabled !== undefined ? configData.enabled : true,
        icon: configData.icon || "file",
        sections: configData.sections || [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        version: "2.0"
      });

      // Save to Firestore using the config ID as document ID
      const docRef = doc(intakeConfigsRef, configId);
      await setDoc(docRef, configToSave);

      // Add to local state
      configs.value[configId] = { ...configToSave };

      logger.info('[FormBuilder] New configuration added successfully', {
        configId,
        name: configData.name
      });

      return { success: true };

    } catch (err) {
      logger.error('[FormBuilder] Failed to add configuration:', {
        configId,
        error: err.message
      });
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Delete a configuration from Firestore
   * @param {string} configId - The ID of the configuration to delete
   */
  async function deleteConfiguration(configId) {
    loading.value = true;
    error.value = null;

    try {
      logger.info('[FormBuilder] Deleting configuration', { configId });

      const docRef = doc(intakeConfigsRef, configId);
      await deleteDoc(docRef);

      // Remove from local state
      delete configs.value[configId];

      logger.info('[FormBuilder] Configuration deleted successfully', { configId });
      return { success: true };

    } catch (err) {
      logger.error('[FormBuilder] Failed to delete configuration:', {
        configId,
        error: err.message
      });
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Generate initial form data structure from a configuration
   * @param {string} formId - The ID of the configuration to use
   * @returns {Object} The initial form data structure with empty values
   */
  function generateFormData(formId) {
    const config = getConfiguration(formId);
    if (!config || !config.sections) {
      logger.warn('[FormBuilder] No configuration found for form', { formId });
      return {};
    }

    try {
      // Create a deep copy of the sections structure with empty values
      const formData = JSON.parse(JSON.stringify(config.sections));

      // Reset all field values to empty state
      formData.forEach(section => {
        // Pricing calculator sections carry their config (rate snapshot) and
        // get fresh default selections (with computed totals) instead of field resets
        if (section.type === 'pricing-calculator' && section.calculator) {
          const values = initCalculatorSelections(section.calculator);
          const totals = computeCalculatorTotals(section.calculator, values);
          section.selections = {
            values,
            lineItems: totals.lineItems,
            total: totals.total,
            currency: totals.currency,
            computedAt: null
          };
          return;
        }
        if (section.fields) {
          section.fields.forEach(field => {
            if (field.input) {
              // Reset the value to empty based on field type
              if (field.type === 'checkbox' && field.input.options) {
                // For checkboxes, set all options to unchecked
                field.input.options.forEach(option => {
                  option.input.value = '';
                });
              } else {
                // For all other field types, set value to empty string
                field.input.value = '';
              }
            }
          });
        }
      });

      logger.info('[FormBuilder] Generated initial form data', {
        formId,
        sectionCount: formData.length,
        totalFields: formData.reduce((total, section) =>
          total + (section.fields ? section.fields.length : 0), 0
        )
      });

      return formData;
    } catch (_error) {
      logger.error('[FormBuilder] Failed to generate form data:', {
        formId,
        error: _error.message
      });
      return {};
    }
  }

  /**
   * Reset the store state
   */
  function reset() {
    loading.value = false;
    error.value = null;
    initialized.value = false;
    configs.value = {};
  }

  return {
    // State
    loading,
    error,
    initialized,
    configs,

    // Actions
    loadConfigurations,
    saveConfiguration,
    addConfiguration,
    deleteConfiguration,
    getConfiguration,
    generateFormData,
    reset
  };
});
